import {
  AbstractMesh,
  ArcRotateCamera,
  Color3,
  Color4,
  Engine,
  HemisphericLight,
  Tools,
  Vector3,
  Scene,
  PointerInfo,
  EventState,
  PointerEventTypes,
  Animation,
} from '@babylonjs/core'
import { MaterialManager } from './materials/materialManager'
import EventEmitter from '../utils/EventEmitter'
import store from '../store'
import { uploadThumbnailAsync } from '../services/thumbnailsService'
import { getBuildingBoundingBox } from './utils'
import { ClusteringManager } from './clusteringManager'
import { AdvancedDynamicTexture } from '@babylonjs/gui'

export class SceneManager {
  private static instance: SceneManager | null = null
  private canvas?: HTMLCanvasElement
  private engine?: Engine | null
  private scene?: Scene
  private camera?: ArcRotateCamera
  private light1?: HemisphericLight
  private eventEmitter?: EventEmitter | null
  private resizeListener?: (this: Window, ev: UIEvent) => any
  private wheelListener?: (event: WheelEvent) => any
  private cameraZoomTargetListener?: (
    data: PointerInfo,
    event: EventState,
  ) => void

  private _isCameraLocked: boolean = false

  get isCameraLocked() {
    return this._isCameraLocked
  }

  private _measurementsUI?: AdvancedDynamicTexture

  get measurementsUI() {
    return this._measurementsUI
  }

  set isMeasurementsUIVisible(visible: boolean) {
    if (!this._measurementsUI) {
      return
    }

    const lineMeasurements = store.getState().measurement.lineMeasurements
    this._measurementsUI.getDescendants(true).forEach((control) => {
      const id = control.metadata.id
      const measurement = id ? lineMeasurements[id] : null
      if (measurement) {
        control.isVisible = visible && measurement.isVisible
        // TODO: Maybe update children visibility too
      }
    })
  }

  private static _sceneInstanceId: number = 0

  static getInstance(): SceneManager {
    if (!SceneManager.instance) {
      SceneManager.instance = new SceneManager()
    }
    return SceneManager.instance
  }

  static sceneInstanceId(): number {
    return SceneManager._sceneInstanceId
  }

  getEngine() {
    return this.engine
  }

  getScene() {
    return this.scene
  }

  getCamera() {
    return this.camera
  }

  getLight() {
    return this.light1
  }

  getEventEmitter() {
    return this.eventEmitter
  }

  async getScreenshot(width?: number, height?: number) {
    return Tools.CreateScreenshotAsync(this.engine!, this.camera!, {
      precision: 1,
      width,
      height,
    })
  }

  updateThumbnail() {
    this.scene?.onReadyObservable.addOnce(async () => {
      const selectedFile = store.getState().organization.selectedFile
      if (!this.canvas || !selectedFile || selectedFile.thumbnail_path) {
        return
      }

      const oldWidth = this.canvas.width
      this.canvas.width = 800
      const oldHeight = this.canvas.height
      this.canvas.height = 600

      this.engine?.resize()
      this.scene?.render()

      const data = await this.getScreenshot(800, 600)

      const mimeType = data.match(/^data:(.*?);base64,/)?.[1]
      if (!mimeType) {
        throw new Error('Invalid MIME Type')
      }

      const base64Data = data.replace(/^data:.+;base64,/, '')
      const byteCharacters = atob(base64Data)
      const byteArray = new Uint8Array(byteCharacters.length)
      for (let i = 0; i < byteCharacters.length; i++) {
        byteArray[i] = byteCharacters.charCodeAt(i)
      }

      const thumbnail = new File(
        [byteArray],
        `${selectedFile.id}_${selectedFile.name}_${Date.now()}_thumbnail.png`,
        { type: mimeType },
      )

      store.dispatch<any>(
        uploadThumbnailAsync({ fileId: selectedFile.id, thumbnail }),
      )

      this.canvas.width = oldWidth
      this.canvas.height = oldHeight

      this.engine?.resize()
      this.scene?.render()
    })
  }

  initialize(canvas: HTMLCanvasElement, eventEmitter: EventEmitter) {
    this.eventEmitter = eventEmitter
    if (!this.engine) {
      this.canvas = canvas
      this.engine = new Engine(canvas, true, {
        antialias: true,
        stencil: true,
      })

      this.scene = new Scene(this.engine)
      this.scene.clearColor = Color4.FromColor3(Color3.White())
      this.scene.useRightHandedSystem = true
      this.incrementSceneInstanceId()

      this.camera = new ArcRotateCamera(
        'Camera',
        -Math.PI / 2,
        Math.PI / 3,
        125,
        new Vector3(0, 0, 0),
        this.scene,
      )
      this.camera.attachControl(this.canvas, true)
      this.camera.wheelPrecision = 0.5
      this.camera.panningSensibility = 200
      this.camera.zoomToMouseLocation = true

      this.light1 = new HemisphericLight(
        'hemiLight',
        new Vector3(1, 0, 1),
        this.scene,
      )
      this.light1.groundColor = Color3.White()
      this.light1.intensity = 0.87

      this._measurementsUI = AdvancedDynamicTexture.CreateFullscreenUI(
        'measurementsUI',
        true,
        this.scene,
      )

      this.resizeListener = () => {
        if (!this.engine) {
          return
        }

        this.engine.resize()
      }

      this.wheelListener = (event: WheelEvent) => {
        if (event.ctrlKey) {
          event.preventDefault()
          const delta = event.deltaY
          if (this.camera) {
            this.camera.radius += delta * 0.2
          }
        }
      }

      this.cameraZoomTargetListener = (
        data: PointerInfo,
        event: EventState,
      ) => {
        if (data.type === PointerEventTypes.POINTERDOUBLETAP && this.camera) {
          this.camera.zoomToMouseLocation = !this.camera?.zoomToMouseLocation

          if (!this.camera.zoomToMouseLocation) {
            const buildingBoundingBox = getBuildingBoundingBox()
            if (buildingBoundingBox) {
              const cameraTarget = Vector3.Lerp(
                buildingBoundingBox.min,
                buildingBoundingBox.max,
                0.5,
              )

              this.camera.setTarget(cameraTarget, false, false, false)
            }
          }
        }
      }

      window.addEventListener('resize', this.resizeListener)
      canvas.addEventListener('wheel', this.wheelListener, { passive: false })
      this.scene.onPointerObservable.add(this.cameraZoomTargetListener)
    }
  }

  startRenderLoop(): void {
    this.engine!.runRenderLoop(() => {
      this.scene!.render()
    })
  }

  stopRenderLoop(): void {
    this.engine!.stopRenderLoop()
  }

  setCameraLock(lock: boolean) {
    if (!this.camera) {
      return
    }

    // if camera is unlocked and need to lock
    if (!this.isCameraLocked && lock) {
      this.camera.detachControl()
      this._isCameraLocked = true
    }
    // if camera is locked and need to unlock
    else if (this.isCameraLocked && !lock) {
      this.camera.attachControl()
      this._isCameraLocked = false
    }
  }

  removeMeshAndDispose(sceneMesh: AbstractMesh) {
    const materialManager = MaterialManager.getInstance()
    const parentAndChildren = [sceneMesh, ...sceneMesh.getChildMeshes()]

    for (const mesh of parentAndChildren) {
      if (mesh.actionManager) {
        mesh.actionManager.dispose()
      }

      if (mesh.material) {
        if (!materialManager.isMaterialShared(mesh)) {
          mesh.material.getBindedMeshes()
          materialManager.removeMaterial(mesh.material.name)
        }
      }
    }

    sceneMesh.dispose()
  }

  isActualSceneInstance(sceneInstanceId: number | undefined) {
    return sceneInstanceId === SceneManager._sceneInstanceId
  }

  reportError(sceneInstanceId: number, error: any) {
    if (this.isActualSceneInstance(sceneInstanceId)) {
      console.error(error)
    }
  }

  animateCameraTo(targetPosition: Vector3) {
    const targetAnim = new Animation(
      'targetAnimation',
      'target',
      60,
      Animation.ANIMATIONTYPE_VECTOR3,
      Animation.ANIMATIONLOOPMODE_CONSTANT,
    )
    const targetKeys = [
      { frame: 0, value: this.camera?.target.clone() },
      { frame: 30, value: targetPosition },
    ]
    targetAnim.setKeys(targetKeys)

    const radiusAnim = new Animation(
      'radiusAnimation',
      'radius',
      60,
      Animation.ANIMATIONTYPE_FLOAT,
      Animation.ANIMATIONLOOPMODE_CONSTANT,
    )
    const radiusKeys = [
      { frame: 0, value: this.camera?.radius },
      { frame: 30, value: 30 },
    ]
    radiusAnim.setKeys(radiusKeys)

    this.camera!.animations = [targetAnim, radiusAnim]
    this.scene?.beginAnimation(this.camera, 0, 30, false)
  }

  dispose(): void {
    window.removeEventListener('resize', this.resizeListener!)
    this.canvas?.removeEventListener('wheel', this.wheelListener!)
    this.scene?.onPointerObservable.removeCallback(
      this.cameraZoomTargetListener!,
    )
    this.stopRenderLoop()
    this.scene!.dispose()
    this.engine!.dispose()
    this.engine = null
    this.eventEmitter = null
    this.measurementsUI?.dispose()
    ClusteringManager.dispose()
    SceneManager.instance = null
  }

  private incrementSceneInstanceId(): void {
    SceneManager._sceneInstanceId++
  }
}
